/* eslint-disable react-compiler/react-compiler */
import { useListEquipmentSizesQuery } from '@/common/infrastructure/gql/generated/schema';
import { AutocompleteOption, useAutocomplete } from '@/common/presentation/hooks/useAutocomplete';
import { filterEquipmentSizes } from '@/jobs/application/utils/equipmentSizeUtils';

/**
 * Type representing an equipment size from the GraphQL query result
 */
type EquipmentSize = NonNullable<
  NonNullable<NonNullable<ReturnType<typeof useListEquipmentSizesQuery>['data']>['equipmentSizes']>['items']
>[number];

export interface UseListEquipmentSizesAutocompleteParams {
  searchTerm?: string;
  debounceMs?: number;
  onEquipmentSizeSelect?: (option: AutocompleteOption) => void;
  skipQuery?: boolean;
}

/**
 * Hook for implementing equipment size autocomplete functionality
 *
 * @param params - Configuration object
 * @param params.searchTerm - The current search term to filter equipment sizes by (default: '')
 * @param params.debounceMs - Debounce time in milliseconds for search term updates (default: 300ms)
 * @param params.onEquipmentSizeSelect - Optional callback when an equipment size is selected
 * @param params.skipQuery - When true, the query is not executed (default: false)
 * @returns Object containing equipment size options, loading state, error state, and filter function
 */
export const useListEquipmentSizesAutocomplete = ({
  searchTerm = '',
  debounceMs = 300,
  onEquipmentSizeSelect: onSelect,
  skipQuery = false
}: UseListEquipmentSizesAutocompleteParams = {}) => {
  return useAutocomplete<EquipmentSize>({
    searchTerm,
    debounceMs,
    onSelect,
    query: (search?: string) => {
      const { data, loading, error } = useListEquipmentSizesQuery({
        variables: {
          skip: 0,
          where: {
            or: [
              {
                name: {
                  contains: search ?? searchTerm
                }
              }
            ]
          }
        },
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
        skip: skipQuery
      });
      return {
        data: { items: filterEquipmentSizes(data?.equipmentSizes?.items ?? []) },
        loading: loading,
        error: error ?? null
      };
    },
    transform: (item) => ({
      value: item.id,
      label: item.name ?? 'Unknown Size'
    })
  });
};
